import { Component } from '@theme/component';
import { cycleFocus } from '@theme/focus';

/**
 * Switches product carousels when a collection tab is selected.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLAnchorElement} [viewAll]
 *
 * @extends {Component<Refs>}
 */
class ProductCollectionsCarousel extends Component {
  requiredRefs = ['tabs', 'panels'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
    this.#syncViewAll(this.#selectedIndex());
  }

  /**
   * @param {number} targetIndex
   * @param {PointerEvent} [event]
   */
  select(targetIndex, event) {
    const index = Number(targetIndex);
    const { tabs, panels } = this.refs;

    if (!tabs?.length || !panels?.length) return;

    if (event instanceof PointerEvent || event instanceof MouseEvent) {
      const isModifiedClick =
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

      if (!isModifiedClick) {
        event.preventDefault();
      }
    }

    this.#activate(index);
  }

  /**
   * @returns {number}
   */
  #selectedIndex() {
    const { tabs } = this.refs;
    if (!tabs?.length) return 0;

    const index = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return index >= 0 ? index : 0;
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const { tabs, panels } = this.refs;
    if (!tabs?.length || !panels?.length) return;

    const selectedIndex = Math.max(0, Math.min(index, tabs.length - 1));

    for (const [tabIndex, tab] of tabs.entries()) {
      const selected = tabIndex === selectedIndex;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }

    for (const [panelIndex, panel] of panels.entries()) {
      if (panelIndex === selectedIndex) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    }

    this.#syncViewAll(selectedIndex);
    this.#scrollTabIntoView(tabs[selectedIndex]);
  }

  /**
   * Keep "View all" pointed at the active collection.
   * @param {number} index
   */
  #syncViewAll(index) {
    const { viewAll, tabs } = this.refs;
    if (!(viewAll instanceof HTMLAnchorElement)) return;

    const selectedTab = tabs?.[index];
    const href = selectedTab instanceof HTMLAnchorElement ? selectedTab.getAttribute('href') : null;

    if (!href || href === '#') {
      viewAll.setAttribute('href', '#');
      viewAll.setAttribute('aria-disabled', 'true');
      viewAll.setAttribute('tabindex', '-1');
      return;
    }

    viewAll.setAttribute('href', href);
    viewAll.removeAttribute('aria-disabled');
    viewAll.removeAttribute('tabindex');
  }

  /**
   * On mobile, keep the active collection title visible in the tabs carousel.
   * @param {HTMLElement | undefined} tab
   */
  #scrollTabIntoView(tab) {
    if (!(tab instanceof HTMLElement)) return;

    const tablist = tab.closest('.product-list-collections-tabs__tablist');
    if (!(tablist instanceof HTMLElement)) return;
    if (tablist.scrollWidth <= tablist.clientWidth) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const tabRect = tab.getBoundingClientRect();
    const listRect = tablist.getBoundingClientRect();
    const nextLeft =
      tablist.scrollLeft + (tabRect.left - listRect.left) - (listRect.width - tabRect.width) / 2;

    tablist.scrollTo({
      left: Math.max(0, nextLeft),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    let modifier = 0;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        modifier = 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        modifier = -1;
        break;
      case 'Home':
        event.preventDefault();
        tabs[0]?.focus();
        this.#activate(0);
        return;
      case 'End':
        event.preventDefault();
        tabs[tabs.length - 1]?.focus();
        this.#activate(tabs.length - 1);
        return;
      default:
        return;
    }

    event.preventDefault();
    cycleFocus(tabs, modifier);

    const activeTab = tabs.find((tab) => tab === document.activeElement);
    const activeIndex = activeTab ? tabs.indexOf(activeTab) : -1;

    if (activeIndex >= 0) {
      this.#activate(activeIndex);
    }
  }
}

if (!customElements.get('product-collections-carousel-component')) {
  customElements.define('product-collections-carousel-component', ProductCollectionsCarousel);
}
